import { Device, getApiUrl } from "@app/shared";
import { PropsWithChildren, useMemo, useState } from "react";
import { Icons, Select } from "../../../shared";

type FacilityCameraFallbackProps = {
    devices: Device[];
};

/**
 * Stands in for the map at a facility with no coordinates. The map needs a lat/lng to center on;
 * without one there's nothing to draw, so the most recent camera snapshot is shown instead — it's
 * still the picture of the lot the count refers to.
 *
 * Children render on top, so the occupancy overlay sits in the same place it would on the map.
 */
export const FacilityCameraFallback: React.FC<PropsWithChildren<FacilityCameraFallbackProps>> = ({ devices, children }) => {
    const withPhotos = useMemo(
        () => devices.filter(d => !d.isDeleted && (d.devicePhotoUrl?.length ?? 0) > 0),
        [devices]);
    const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

    const selected = withPhotos.find(d => d.id === selectedId) ?? withPhotos[0];

    return (
        <div className="relative h-full w-full bg-background-page-highlight-gray flex flex-col">
            <div className="flex items-center gap-3 px-3 py-2 text-sm text-semi-bright-text border-b border-separators-page">
                <Icons.MapMarker className="w-4 h-4 shrink-0" />
                <span className="flex-grow">
                    Map view needs coordinates for this location. Showing the latest camera snapshot instead.
                </span>
                {withPhotos.length > 1 &&
                    <Select
                        value={selected?.id}
                        onChange={(e) => setSelectedId(e.target.value)}
                        options={withPhotos.map(d => ({ label: d.name ?? "Device", value: d.id ?? "" }))}
                    />
                }
            </div>

            <div className="relative flex-grow min-h-0">
                {selected ?
                    <img
                        src={getApiUrl(selected.devicePhotoUrl!)}
                        alt={`Latest snapshot from ${selected.name ?? "device"}`}
                        className="h-full w-full object-contain"
                    />
                    :
                    <div className="h-full flex items-center justify-center text-sm text-semi-bright-text">
                        No camera snapshot available from any device at this location.
                    </div>
                }
                {children}
            </div>
        </div>
    );
};
